import type { MultipleSetScoreInputDto } from "@/modules/scores/scoreForms/gateway/scoreDto"
import type { CommentFormikType, ScoreSetCompletionFormikType } from "@/modules/scores/scoreForms/scoreFormikTypes"
import { word } from "@modules/core/utils/i18n"
import { convertTimeStringToTimeValue } from "@modules/duration/timeConverter"
import { Language } from "@modules/language/utils/languageUtils"
import {
  ScoreDifficultyEnum,
  ScoreResultType,
  ScoreType,
  type ScoreResultDraftType,
  type ScoreSetConfiguration,
} from "@modules/scores/scoreTypes"
import { convertTimeChrono } from "@modules/utils/chronoConvertUtils"
import { convertDistanceFromMeter, convertDistanceFromMeterText } from "@modules/utils/distanceConversionUtils"
import { type HUUnit } from "@modules/utils/unitType"
import {
  convertWeightFromGram,
  convertWeightFromGramText,
  convertWeightFromInput,
} from "@modules/utils/weightConvertionUtils"
import convert from "convert-units"

export function getScoreTypesArray(): { label: string; value: ScoreType }[] {
  return Object.values(ScoreType).map((scoreType) => ({
    label: getScoreTypeLabel(scoreType),
    value: scoreType,
  }))
}

export function getScoreTypeLabel(scoreType: ScoreType): string {
  switch (scoreType) {
    case ScoreType.NO_SCORE:
      return word("global.score.no_score")
    case ScoreType.TEXT_FROM_ATHLETE:
      return word("global.score.text_from_athlete")
    case ScoreType.TIME:
      return word("global.score.time.label")
    case ScoreType.ROUND_AND_REPETITIONS:
      return word("global.score.round_and_repetitions.label")
    case ScoreType.REPS:
      return word("global.score.repetitions.label")
    case ScoreType.WEIGHT:
      return word("global.score.weight.label")
    case ScoreType.CALORIES:
      return word("global.score.calories.label")
    case ScoreType.DISTANCE:
      return word("global.score.distance.label")
    case ScoreType.WATTS:
      return word("global.score.watt.label")
    default:
      return ""
  }
}

export function getScoreResultTypesArray(): {
  label: string
  value: ScoreResultDraftType
}[] {
  return [
    { label: word("global.scoreResult.total.label"), value: ScoreResultType.TOTAL },
    { label: word("global.scoreResult.highest.label"), value: ScoreResultType.HIGHEST },
    { label: word("global.scoreResult.lowest.label"), value: ScoreResultType.LOWEST },
    { label: word("global.scoreResult.average.label"), value: ScoreResultType.AVERAGE },
  ]
}

export const getDisplayedScore = (
  score: number,
  scoreAdditional: number | null,
  scoreType: ScoreType,
  timeCapExceeded: boolean,
  unit: HUUnit,
  language: Language,
) => {
  if (timeCapExceeded) return `TC + ${score} ${word("global.score.repetition.unit", { count: score })} `

  switch (scoreType) {
    case ScoreType.TIME:
      return convertTimeChrono(score)
    case ScoreType.CALORIES:
      return `${score} cal`
    case ScoreType.WATTS:
      return `${score} W`
    case ScoreType.DISTANCE: {
      return convertDistanceFromMeterText(score, unit, language)
    }
    case ScoreType.REPS:
      return `${score} ${word("global.score.repetition.unit", {
        count: score,
      })}`
    case ScoreType.ROUND_AND_REPETITIONS: {
      const { roundLabel, repLabel } = getRoundRepLabel(score, scoreAdditional)

      if (roundLabel && repLabel) {
        return `${roundLabel} ${word("global.score.round_and_repetitions.separator")} ${repLabel}`
      } else {
        return `${roundLabel ?? repLabel}`
      }
    }
    case ScoreType.WEIGHT:
      return convertWeightFromGramText(score, unit, language)
    case ScoreType.NO_SCORE:
    case ScoreType.TEXT_FROM_ATHLETE:
      return ""
  }
}

export const getRoundRepLabel = (score: number, scoreAdditional: number | null) => {
  const roundLabel = score ? `${score} ${word("global.score.round.unit", { count: score })}` : undefined
  const repLabel = scoreAdditional
    ? `${scoreAdditional} ${word("global.score.repetition.unit", { count: scoreAdditional })}`
    : undefined

  return { roundLabel, repLabel }
}

export const getScoreResultTypeCaption = (
  scoreResultType: ScoreResultDraftType | ScoreResultType,
  shortLabel: boolean = false,
) => {
  if (scoreResultType.startsWith("SET_N_FOR_SCOREBOARD_")) {
    const splitValue = scoreResultType.split("_")
    const indexString = splitValue[splitValue.length - 1]
    if (indexString === undefined) {
      throw new Error("Invalid scoring option value")
    }
    const index = parseInt(indexString)
    return shortLabel
      ? word("global.scoreResult.setNForScoreboard.label", { index: index + 1 })
      : word("global.scoreResult.setNForScoreboard.caption", { index: index + 1 })
  }

  if (scoreResultType === ScoreResultType.TOTAL) {
    return shortLabel ? word("global.scoreResult.total.label") : word("global.scoreResult.total.caption")
  } else if (scoreResultType === ScoreResultType.HIGHEST) {
    return shortLabel ? word("global.scoreResult.highest.label") : word("global.scoreResult.highest.caption")
  } else if (scoreResultType === ScoreResultType.LOWEST) {
    return shortLabel ? word("global.scoreResult.lowest.label") : word("global.scoreResult.lowest.caption")
  } else if (scoreResultType === ScoreResultType.AVERAGE) {
    return shortLabel ? word("global.scoreResult.average.label") : word("global.scoreResult.average.caption")
  } else {
    return ""
  }
}

export const getScoreUnit = (type: ScoreType.WEIGHT | ScoreType.DISTANCE, unit: HUUnit) => {
  switch (type) {
    case ScoreType.WEIGHT:
      return unit === "IMPERIAL" ? "lb" : "kg"
    case ScoreType.DISTANCE:
      return unit === "IMPERIAL" ? "mi" : "m"
  }
}
export const convertScoreFromInput = (input: number | string, scoreType: ScoreType, unit: HUUnit): number => {
  switch (scoreType) {
    case ScoreType.TIME:
      return typeof input === "string" ? (convertTimeStringToTimeValue("00m 00s", input) ?? 0) : input
    case ScoreType.CALORIES:
    case ScoreType.REPS:
    case ScoreType.WATTS:
    case ScoreType.ROUND_AND_REPETITIONS:
      return typeof input === "number" ? input : 0
    case ScoreType.DISTANCE:
      return convert(typeof input === "number" ? input : undefined)
        .from(unit === "IMPERIAL" ? "mi" : "m")
        .to("mm")
    case ScoreType.WEIGHT:
      return convertWeightFromInput(typeof input === "number" ? input : 0, unit)
    case ScoreType.NO_SCORE:
    case ScoreType.TEXT_FROM_ATHLETE:
      return 0
  }
}

export const convertScoreToInputValue = (score: number | null, scoreType: ScoreType, unit: HUUnit) => {
  switch (scoreType) {
    case ScoreType.TIME:
      return score ?? 0
    case ScoreType.CALORIES:
    case ScoreType.REPS:
    case ScoreType.WATTS:
      return score ?? 0
    case ScoreType.ROUND_AND_REPETITIONS:
      return score ?? 0
    case ScoreType.DISTANCE:
      return score ? convertDistanceFromMeter(score, unit) : 0
    case ScoreType.WEIGHT:
      return score ? convertWeightFromGram(score, unit) : 0
    case ScoreType.NO_SCORE:
    case ScoreType.TEXT_FROM_ATHLETE:
      return 0
    default:
      return 0
  }
}

export const getScoreSetCompletionResultingScore = (
  scoreSetCompletions: ScoreSetCompletionFormikType[] | null,
  scoreSetConfigurations: ScoreSetConfiguration[] | null,
  scoringOption: ScoreResultType | null,
  unit: HUUnit,
): { valueDecimal: number | null; additionalValueDecimal: number | null } => {
  if (!scoreSetCompletions) return { valueDecimal: null, additionalValueDecimal: null }

  if (
    scoreSetCompletions.some(
      (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.TIME && scoreSetCompletion.timeCapExceeded,
    )
  ) {
    return {
      valueDecimal: scoreSetCompletions
        .filter(
          (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.TIME && scoreSetCompletion.timeCapExceeded,
        )
        .map((scoreSetCompletion) => scoreSetCompletion.score ?? 0)
        .reduce((a, b) => a + b, 0),
      additionalValueDecimal: null,
    }
  }
  if (scoringOption === ScoreResultType.SET_N_FOR_SCOREBOARD && scoreSetConfigurations) {
    const scoreSetConfigurationForScoring = scoreSetConfigurations.find(
      (scoreSetConfiguration) => scoreSetConfiguration.useForScoring,
    )
    if (!scoreSetConfigurationForScoring) throw new Error("Score set configuration not found")

    const scoreSetCompletion = scoreSetCompletions.find(
      (scoreSetCompletion) => scoreSetCompletion.scoreSetConfigurationId === scoreSetConfigurationForScoring.id,
    )
    if (!scoreSetCompletion) throw new Error("Score set completion not found")

    return {
      valueDecimal: scoreSetCompletion.score,
      additionalValueDecimal: null,
    }
  }
  if (scoringOption === ScoreResultType.HIGHEST) {
    return {
      valueDecimal: convertScoreFromInput(
        scoreSetCompletions
          .map((scoreSetCompletion) => scoreSetCompletion.score)
          .reduce((a, b) => Math.max(a ?? 0, b ?? 0), 0) ?? 0,
        scoreSetCompletions[0]?.scoreType ?? ScoreType.NO_SCORE,
        unit,
      ),
      additionalValueDecimal: null,
    }
  }
  if (scoringOption === ScoreResultType.LOWEST) {
    return {
      valueDecimal: convertScoreFromInput(
        scoreSetCompletions
          .map((scoreSetCompletion) => scoreSetCompletion.score)
          .reduce((a, b) => Math.min(a ?? 0, b ?? 0)) ?? 0,
        scoreSetCompletions[0]?.scoreType ?? ScoreType.NO_SCORE,
        unit,
      ),
      additionalValueDecimal: null,
    }
  }
  if (scoringOption === ScoreResultType.AVERAGE) {
    if (
      scoreSetCompletions.every(
        (scoreSetCompletion) => scoreSetCompletion.scoreType === ScoreType.ROUND_AND_REPETITIONS,
      )
    ) {
      const averageRound =
        scoreSetCompletions.map((scoreSetCompletion) => scoreSetCompletion.score ?? 0).reduce((a, b) => a + b, 0) /
        scoreSetCompletions.length
      const roundedAverageRound = Math.round(averageRound * 10) / 10
      const averageRep =
        scoreSetCompletions
          .map((scoreSetCompletion) => scoreSetCompletion.scoreAdditional ?? 0)
          .reduce((a, b) => a + b, 0) / scoreSetCompletions.length
      const roundedAverageRep = Math.round(averageRep * 10) / 10

      return {
        valueDecimal: roundedAverageRound,
        additionalValueDecimal: roundedAverageRep,
      }
    } else {
      return {
        valueDecimal: convertScoreFromInput(
          scoreSetCompletions && scoreSetCompletions.length > 0
            ? scoreSetCompletions
                .map((scoreSetCompletion) => scoreSetCompletion.score ?? 0)
                .reduce((a, b) => a + b, 0) / scoreSetCompletions.length
            : 0,
          scoreSetCompletions[0]?.scoreType ?? ScoreType.NO_SCORE,
          unit,
        ),
        additionalValueDecimal: null,
      }
    }
  }
  if (scoringOption === ScoreResultType.TOTAL) {
    return {
      valueDecimal: convertScoreFromInput(
        scoreSetCompletions.map((scoreSetCompletion) => scoreSetCompletion.score ?? 0).reduce((a, b) => a + b, 0) ?? 0,
        scoreSetCompletions[0]?.scoreType ?? ScoreType.NO_SCORE,
        unit,
      ),
      additionalValueDecimal: null,
    }
  }

  if (scoringOption === null && scoreSetCompletions.length === 1 && !!scoreSetCompletions[0]) {
    return {
      valueDecimal: convertScoreFromInput(
        scoreSetCompletions[0].score ?? 0,
        scoreSetCompletions[0].scoreType ?? ScoreType.NO_SCORE,
        unit,
      ),
      additionalValueDecimal: scoreSetCompletions[0].scoreAdditional ?? null,
    }
  }

  throw new Error("Invalid scoring option")
}

export const convertCommentFromDraft = (draft: CommentFormikType): MultipleSetScoreInputDto => {
  return {
    scoreValueDecimal: 0,
    scoreAdditionalValueDecimal: 0,
    difficulty: ScoreDifficultyEnum.RX,
    note: draft.note ?? null,
    noteAndMediaIsVisible: draft.noteAndMediaIsVisible,
    medias: draft.medias.map((media) => ({
      url: !!media.url && media.url !== "" ? media.url : null,
      key: media.key,
      type: media.type,
      filename: media.filename ?? null,
    })),
    scoreSetCompletions: [],
    personalRecord: null,
  }
}
